import React from 'react';
import '../styles/LegalPages.css';

function PrivacyPolicy() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <section id="privacy" className="legal-page section">
      <div className="container">
        <div className="legal-content">
          <h1 className="legal-title">Privacy Policy</h1>
          <p className="legal-updated">Last Updated: January 2025</p>

          <div className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Welcome to FortIf. We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our AI-powered resume screening platform.
            </p>
            <p>
              FortIf is operated by FortIf Technologies, located at 806, Lokmanya Tilak Road, Near Borivali West Station, Borivali West, Mumbai, Maharashtra 400092, India.
            </p>
          </div>

          <div className="legal-section">
            <h2>2. Information We Collect</h2>
            <h3>2.1 Information You Provide</h3>
            <p>We collect information that you voluntarily provide to us, including:</p>
            <ul>
              <li>Account information (name, email address, company details)</li>
              <li>Resume and candidate data uploaded for screening</li>
              <li>Job descriptions and hiring requirements</li>
              <li>Payment and billing information</li>
              <li>Communication preferences and feedback</li>
            </ul>

            <h3>2.2 Automatically Collected Information</h3>
            <p>When you use our platform, we automatically collect:</p>
            <ul>
              <li>Device and browser information</li>
              <li>IP address and location data</li>
              <li>Usage data and analytics</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information to:</p>
            <ul>
              <li>Provide and maintain our AI resume screening services</li>
              <li>Process and analyze resumes according to your job requirements</li>
              <li>Improve our AI algorithms and screening accuracy</li>
              <li>Send service updates, technical notices, and support messages</li>
              <li>Respond to your inquiries and provide customer support</li>
              <li>Detect and prevent fraud, security breaches, and technical issues</li>
              <li>Comply with legal obligations and enforce our terms</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>4. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your information:
            </p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Access controls and authentication mechanisms</li>
              <li>Secure data centers with physical and digital safeguards</li>
              <li>Employee training on data protection and privacy</li>
            </ul>
            <p>
              However, no method of transmission over the Internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
            </p>
          </div>

          <div className="legal-section">
            <h2>5. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share information in the following circumstances:</p>
            <ul>
              <li><strong>With your consent:</strong> When you explicitly authorize us to share specific information</li>
              <li><strong>Service providers:</strong> Third-party vendors who assist in operating our platform (cloud hosting, analytics, payment processing)</li>
              <li><strong>Legal requirements:</strong> When required by law, court order, or government regulations</li>
              <li><strong>Business transfers:</strong> In connection with mergers, acquisitions, or sale of assets</li>
              <li><strong>Protection of rights:</strong> To protect our rights, property, safety, or that of our users</li>
            </ul>
          </div>

          <div className="legal-section">
            <h2>6. Data Retention</h2>
            <p>
              We retain your information for as long as necessary to provide our services and comply with legal obligations. Resume data is retained according to your subscription plan and can be deleted upon request. Account information is retained until you close your account.
            </p>
          </div>

          <div className="legal-section">
            <h2>7. Your Rights and Choices</h2>
            <p>You have the following rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your data</li>
              <li><strong>Data portability:</strong> Request a copy of your data in a structured format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Object to processing:</strong> Object to certain data processing activities</li>
            </ul>
            <p>To exercise these rights, contact us at <a href="mailto:info@fortif.app">info@fortif.app</a></p>
          </div>

          <div className="legal-section">
            <h2>8. Cookies and Tracking</h2>
            <p>
              We use cookies and similar technologies to improve your experience, analyze usage patterns, and provide personalized content. You can control cookies through your browser settings, though disabling cookies may affect platform functionality.
            </p>
          </div>

          <div className="legal-section">
            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.
            </p>
          </div>

          <div className="legal-section">
            <h2>10. Children's Privacy</h2>
            <p>
              Our services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
            </p>
          </div>

          <div className="legal-section">
            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. Your continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </div>

          <div className="legal-section">
            <h2>12. Contact Us</h2>
            <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
            <div className="contact-details">
              <p><strong>FortIf Technologies</strong></p>
              <p>806, Lokmanya Tilak Road</p>
              <p>Near Borivali West Station</p>
              <p>Borivali West, Mumbai</p>
              <p>Maharashtra 400092, India</p>
              <p>Email: <a href="mailto:info@fortif.app">info@fortif.app</a></p>
              <p>Support: <a href="mailto:support@fortif.app">support@fortif.app</a></p>
            </div>
          </div>

          <div className="back-to-top">
            <button onClick={scrollToTop} className="btn btn-secondary">
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default PrivacyPolicy;
